import { PageOrThread } from '@/components/admin/experiences/conversation-thread';
import { ProtocolVignette } from '@/components/admin/experiences/timeline/protocol-vignette';
import type { ReaderFormat } from '@/components/reader';
import type {
  ExperiencePageDraft,
  ExperienceTheme,
  ExperienceVariable,
  ProtocolCard,
} from '@matter_extractor/shared';

/**
 * Une page de la scène, rendue à sa taille d'auteur puis MISE À L'ÉCHELLE
 * (refonte modulaire §9.1 ; maquette `Timeline.dc.html` ; issue #57).
 *
 * The scene shows the same page three times over at two sizes - large in the
 * middle, small on either side - and this is the one component that draws one:
 * the centre and a neighbour differ by a NUMBER and not by a rendering, which
 * is what keeps « la précédente et la suivante sont entières » true. A second
 * component for the small ones would have been a second idea of what a page
 * looks like, and the first thing it would have lost is the theme.
 *
 * ## Why a transform, and not a smaller reader
 *
 * `PageReader` is authored for a FORMAT and not for a viewport (`contracts.ts`
 * of the reader: « a format is NOT a breakpoint »), so its reading column, its
 * type steps and its rhythm are fixed numbers. Handed a 200 px box it would
 * paint a 640 px column clipped at 200, i.e. the left third of a page - which
 * looks like a bug and is one. Rendered at its own size inside a `scale()`, it
 * paints the WHOLE page and the browser shrinks the picture: that is what
 * « entières, en petit » asks for, in one CSS property.
 *
 * The wrapper carries the SCALED box so the layout reserves the right space -
 * a transform does not change layout size, which is the classic way a scaled
 * element leaves a hole beside it.
 *
 * ## The centre scrolls, and it costs nothing
 *
 * §9.1: la page au centre « défile dans son cadre si elle est longue ».
 * `.lecteur` already carries `overflow-y: auto`, so the centre needs its height
 * and nothing else. A neighbour is a picture of a whole page at a quarter, so
 * its own scroll is unreachable and harmless: the transform shrinks the
 * scrollbar with the rest.
 */

/** The authored size of a page, per format (§9.1, the mockup's own numbers). */
const PREVIEW_SIZE: Readonly<Record<ReaderFormat, { width: number; height: number }>> = {
  screen: { width: 640, height: 400 },
  phone: { width: 390, height: 844 },
};

/**
 * How much of that size the two places on the scene spend.
 *
 * The screen centre is 1:1 - it is the plate's own 640 × 400 - and a phone is
 * 844 px tall, which no tab can give a preview, so its centre is reduced:
 * « 390 × 844 réduit » is the issue's own wording.
 *
 * The NEIGHBOUR is a quarter, where the plate draws it at 200 px (a third).
 * Measured rather than chosen: at 200 the row is 1232 px before padding, which
 * a 1280 px window does not show whole beside the rail - so the scene opened
 * scrolled, with the page it is meant to put at the centre hard against the
 * left edge. At a quarter the whole line fits a laptop and the row centres
 * itself; narrower than that, it still scrolls, and it is the same scrollbar.
 */
export const PREVIEW_SCALE: Readonly<Record<ReaderFormat, { centre: number; neighbour: number }>> =
  {
    screen: { centre: 1, neighbour: 0.25 },
    phone: { centre: 0.6, neighbour: 0.18 },
  };

export interface PagePreviewProps {
  readonly page: ExperiencePageDraft;
  /** The experience's theme. The reader merges the page's own overrides itself. */
  readonly theme: ExperienceTheme;
  readonly format: ReaderFormat;
  readonly scale: number;
  /** The protocol page's reference resolved against the library, or null (§9.5). */
  readonly protocolCard: ProtocolCard | null;
  /** Whether that library has been read - nothing is claimed before it (§4). */
  readonly protocolsRead: boolean;
  /** What a variable is NAMED - the scene paints the pill, never a value (§8.6, D5). */
  readonly variableNames: (variable: ExperienceVariable) => string;
}

export function PagePreview({
  page,
  theme,
  format,
  scale,
  protocolCard,
  protocolsRead,
  variableNames,
}: PagePreviewProps) {
  const { width, height } = PREVIEW_SIZE[format];
  const box = { width: width * scale, height: height * scale };

  /*
    The protocol page is NOT scaled, and it is the one exception to the
    paragraph above: it holds no blocks, so there is no authored page to shrink
    - what it draws is a CARD, and a card shrunk by a third is a card nobody can
    read. It is laid out in the scaled box directly, at the size that box gives
    it, which is what `ProtocolVignette`'s two variants are for.
  */
  if (page.kind === 'protocol') {
    return (
      <div style={box} className="overflow-hidden">
        <ProtocolVignette
          reference={page.protocol}
          card={protocolCard}
          resolved={protocolsRead}
          variant={scale >= 0.5 ? 'centre' : 'neighbour'}
        />
      </div>
    );
  }

  return (
    <div style={box} className="relative overflow-hidden">
      <div
        style={{ width, height, transform: `scale(${scale})`, transformOrigin: 'top left' }}
        className="absolute top-0 left-0"
      >
        <PageOrThread
          page={page}
          theme={theme}
          format={format}
          mode="static"
          values={{}}
          variableNames={variableNames}
        />
      </div>
    </div>
  );
}
